import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";

// ---------------------------------------------------------------------------
// Theme (Dark Mode 2026-07) — drei Modi:
//   'light'  — immer hell
//   'dark'   — immer dunkel
//   'system' — folgt prefers-color-scheme des Betriebssystems (Default),
//              inklusive Live-Wechsel per matchMedia-Change-Listener.
// Persistiert unter localStorage["avliana-theme"]. Der aufgelöste Modus setzt
// die Klasse `dark` auf <html> (Tailwind darkMode: 'class'); ein Inline-Skript
// in public/index.html setzt die Klasse bereits VOR React, damit beim Laden
// kein heller Blitz entsteht.
// ---------------------------------------------------------------------------

const STORAGE_KEY = "avliana-theme";
const MODES = ["light", "dark", "system"];

const ThemeContext = createContext(null);

function readStoredMode() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return MODES.includes(v) ? v : "system";
  } catch {
    /* Private-Mode ohne localStorage */
    return "system";
  }
}

function systemPrefersDark() {
  return typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStoredMode);
  const [systemDark, setSystemDark] = useState(systemPrefersDark);

  // OS-Wechsel live übernehmen (nur relevant, solange mode === 'system').
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => setSystemDark(e.matches);
    // Safari < 14 kennt nur addListener/removeListener.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const theme = mode === "system" ? (systemDark ? "dark" : "light") : mode;

  // Klasse auf <html> spiegeln — einzige Stelle, die das DOM anfasst.
  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* Private-Mode ohne localStorage */
    }
  }, [mode]);

  const setMode = useCallback((m) => {
    if (MODES.includes(m)) setModeState(m);
  }, []);

  const value = useMemo(
    () => ({ mode, setMode, theme, isDark: theme === "dark" }),
    [mode, setMode, theme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
